import {
  Breadcrumbs,
  Link,
  Grid,
  Typography,
  CircularProgress
} from "@mui/material";
import { Box } from "@mui/system";
import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { URLS } from "../../../configuration/urls";
import { FormattedMessage } from "react-intl";
// Payment history hidden until payment data is synced with SAP (BO-858).
// Explicitly out of scope for BO-831 (and any ticket until BO-858 lands) —
// an empty/missing Payment History here is expected, not a bug.
// import SubscriptionsTable from "./subscriptions-table";
import BaseFormCard from "../../base/BaseFormCard";
import BaseButton from "../../base/BaseButton";
import APIContext from "../../../services/api/api";
import ManageProfileContext from "../../../services/manage-profile/manageProfile";
import messages_de from "../../../translations/de.json";
import messages_en from "../../../translations/en.json";
import UserContext from "../../../services/user/user";
import {
  getUpgradeWizardPath,
  translateBackendProductName
} from "./subscription-product-config";
import {
  ECAxiosAPI,
  formattedDate,
  formatTime
} from "@eclear-ag/ec-common-ui";
import * as _ from "lodash";

export default function SubscriptionsDetails() {
  const messages = {
    de: messages_de,
    en: messages_en
  };
  const { lang } = useContext(UserContext);
  const { setCurrentSubscriptionView, apiHeader } = useContext(APIContext);
  const { subscriptionDetails } = useContext(ManageProfileContext);
  const navigate = useNavigate();
  const [billingAddress, setBillingAddress] = useState("");
  const [memberSince, setMemberSince] = useState("");
  const upgradeUrl = getUpgradeWizardPath(subscriptionDetails?.productType);
  const hasLoadedSubscriptionDetails = Boolean(subscriptionDetails?.name);
  // A trial is not billed, so the billing-cycle fields (Payment Cycle,
  // Automatically Renews, Payment Method) carry no meaning and are hidden.
  // `product.trial` comes from get-me (userSubscriptions[].product.trial).
  // Anything other than an explicit `true` is treated as paid, so a missing
  // product or flag keeps the full — safer — view.
  const isTrial = _.get(subscriptionDetails, "product.trial", false) === true;

  // A German invoice address needs the company's registered name, which
  // includes its legal form (§14 UStG). Company Name and Legal Form are
  // captured as separate fields at signup, so the name may or may not already
  // carry it — only append when it is actually missing, to avoid "Acme GmbH GmbH".
  // The API is loosely typed here, so treat anything that is not a usable
  // string as absent rather than letting it reach .trim() or a template.
  const asText = (value) =>
    typeof value === "string" || typeof value === "number"
      ? String(value).trim()
      : "";

  const companyNameWithLegalForm = (rawName, rawLegalForm) => {
    const name = asText(rawName);
    const legalForm = asText(rawLegalForm);
    if (!name) return legalForm;
    if (!legalForm) return name;
    const alreadyIncluded = name
      .toLowerCase()
      .endsWith(legalForm.toLowerCase());
    return alreadyIncluded ? name : `${name} ${legalForm}`;
  };

  // Joins the parts that are actually present, so missing or non-string fields
  // never render as "undefined"/"null" in the address line. Returns "" when the
  // address carries nothing usable, so the caller can fall back.
  const formatAddress = (name, address) => {
    if (!address || typeof address !== "object") return "";
    const streetLine = [asText(address.street), asText(address.houseNumber)]
      .filter(Boolean)
      .join(" ");
    const addressParts = [
      streetLine,
      asText(address.postalCode),
      asText(address.city)
    ].filter(Boolean);
    if (!addressParts.length) return "";
    return [name, ...addressParts].filter(Boolean).join(", ");
  };

  const getMerchantData = async (merchantId) => {
    const notAvailable = lang === "de" ? "n.z." : "N/A";
    // Without a merchant id the request could only ever 404 on "merchant/undefined".
    if (merchantId === undefined || merchantId === null || merchantId === "") {
      setMemberSince(notAvailable);
      setBillingAddress(notAvailable);
      return;
    }
    let data;
    try {
      data = (await ECAxiosAPI.get(
        `${URLS.EC_AUTH_BE()}merchant/${merchantId}`,
        { headers: apiHeader() }
      )) ?? {};
    } catch (err) {
      setMemberSince(notAvailable);
      setBillingAddress(notAvailable);
      return;
    }
    // Formatting runs outside the try so a bug here surfaces instead of being
    // swallowed and silently reported to the user as "no address on file".
    setMemberSince(formattedDate(data.initialCreationDate) || notAvailable);
    // The merchant record is the source of truth for the billing address until
    // self-service billing addresses land. Fall back to the primary address
    // when the billing one is absent or carries no usable fields.
    const companyName = companyNameWithLegalForm(data.name, data.legalForm);
    const formatted =
      formatAddress(companyName, data.billingAddress) ||
      formatAddress(companyName, data.primaryAddress);
    setBillingAddress(formatted || notAvailable);
  };

  useEffect(() => {
    getMerchantData(subscriptionDetails?.merchantId);
  }, [subscriptionDetails]);

  if (!hasLoadedSubscriptionDetails) {
    return (
      <Box sx={{ textAlign: "center" }} data-testid="subscription-details-loading">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Breadcrumbs
        aria-label="subscription breadcrumbs"
        style={{ margin: "20px 0px" }}
        sx={{
          "& .MuiBreadcrumbs-separator": {
            mx: "10px",
            display: "flex",
            alignItems: "center"
          }
        }}
        separator={
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M4.818 11.83a1.035 1.035 0 0 0 1.408 1.519l4.982-4.615a1 1 0 0 0 0-1.468L6.226 2.652a1.035 1.035 0 0 0-1.408 1.517L8.945 8l-4.127 3.83z"
              fill="var(--font-heavy-default)"
            />
          </svg>
        }
      >
        <Link
          underline="none"
          color="inherit"
          href="/"
          onClick={(e) => {
            e.preventDefault();
            setCurrentSubscriptionView({
              screen: "subscriptionsList",
              product: ""
            });
            navigate("/spot/manage-profile?tab=subscriptions");
          }}
          style={{
            fontFamily: "Inter",
            fontSize: "14px",
            fontWeight: 600,
            fontStretch: "normal",
            fontStyle: "normal",
            lineHeight: 1.57,
            letterSpacing: "normal",
            textAlign: "left",
            color: "var(--font-medium-default)",
            cursor: "pointer"
          }}
        >
          <FormattedMessage id="overviewSubscriptions" />
        </Link>
        <Link
          data-testid="subscription-name"
          underline="none"
          color="inherit"
          className="text"
          style={{
            fontFamily: "Inter",
            fontSize: "14px",
            fontWeight: 600,
            fontStretch: "normal",
            fontStyle: "normal",
            lineHeight: 1.57,
            letterSpacing: "normal",
            textAlign: "left",
            color: "var(--font-heavy-default)"
          }}
        >
          {translateBackendProductName(
            subscriptionDetails.name,
            messages[lang] ?? messages.en
          )}
        </Link>
      </Breadcrumbs>
      <BaseFormCard
        noForm
        headerExtra={
          <Typography
            component="p"
            data-testid="subscription-payment-details-title"
            sx={{
              m: 0,
              fontFamily: "Inter",
              fontSize: 16,
              fontWeight: 600,
              lineHeight: 1.5,
              color: "var(--font-strong-default) !important"
            }}
          >
            <FormattedMessage
              id="paymentDetailsOf"
              values={{
                name: translateBackendProductName(
                  _.get(subscriptionDetails, "name", ""),
                  messages[lang] ?? messages.en
                )
              }}
            />
          </Typography>
        }
      >
        <Box sx={{ p: 2 }}>
          <Grid container spacing={4}>
            <Grid item xs={12} md={3}>
              <Box className="payment-detail-field">
                <Box className="payment-detail-title">
                  <FormattedMessage id="memberSince" />
                </Box>
                <Box
                  className="payment-detail-description"
                  data-testid="subscribed-since"
                >
                  {formatTime(subscriptionDetails.startDate, "date")}
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} md={3}>
              <Box className="payment-detail-field">
                <Box className="payment-detail-title">
                  <FormattedMessage id="billingAddress" />
                </Box>
                <Box
                  className="payment-detail-description"
                  data-testid="subscription-billing-address"
                >
                  {billingAddress}
                </Box>
              </Box>
            </Grid>
            {!isTrial && (
              <Grid item xs={12} md={3}>
                <Box className="payment-detail-field">
                  <Box className="payment-detail-title">
                    <FormattedMessage id="paymentCycle" />
                  </Box>
                  <Box
                    className="payment-detail-description"
                    data-testid="subscription-payment-cycle"
                  >
                    <FormattedMessage
                      id={_.get(
                        subscriptionDetails,
                        "product.billingPeriodType",
                        "N/A"
                      )}
                    />
                  </Box>
                </Box>
              </Grid>
            )}
            {!isTrial && (
              <Grid item xs={12} md={3}>
                <Box className="payment-detail-field">
                  <Box className="payment-detail-title">
                    <FormattedMessage id="expires" />
                  </Box>
                  <Box
                    className="payment-detail-description"
                    data-testid="subscription-renew"
                  >
                    <FormattedMessage
                      id={_.get(subscriptionDetails, "product.renewalType", "N/A")}
                    />
                  </Box>
                </Box>
              </Grid>
            )}
          </Grid>
          {!isTrial && subscriptionDetails?.paymentMethod ? (
            <Box style={{ marginBlock: "40px" }}>
              <Box className="payment-detail-field">
                <Box className="payment-detail-title">
                  <FormattedMessage id="paymentMethod" />
                </Box>
                <Box
                  className="payment-detail-description"
                  data-testid="subscription-payment-method"
                  sx={{ display: "flex", alignItems: "center", gap: "8px" }}
                >
                  {messages[lang][subscriptionDetails.paymentMethod]}
                </Box>
              </Box>
            </Box>
          ) : (
            <Box style={{ marginBlock: "40px" }}></Box>
          )}
          {/* Shown for every subscription — trial and paid alike. Products with
              no in-app pricing page (e.g. SPOT) render it disabled rather than
              hidden, so the action is discoverable everywhere. */}
          <BaseButton
            data-testid="subscription-upgrade-cta"
            variant="text"
            href={upgradeUrl ?? undefined}
            disabled={!upgradeUrl}
          >
            <FormattedMessage id="subscription.upgradeCta" />
          </BaseButton>
        </Box>
      </BaseFormCard>

      <Box style={{ marginTop: "20px" }}>
        <FormattedMessage id="supportMessage" />
        <a
          href="mailto:support@eclear.com"
          style={{ color: "var(--font-strong-default)" }}
        >
          support@eclear.com
        </a>
      </Box>

      {/* Payment history hidden until payment data is synced with SAP (BO-858). To be re-enabled in a later release.
          Explicitly out of scope for BO-831 (and any ticket until BO-858 lands) — an empty/missing Payment History here is expected, not a bug. */}
      {/* <SubscriptionsTable
        paymentMethod={subscriptionDetails.paymentMethodDefault}
      /> */}

      {/* <Box display={"flex"} justifyContent={"center"} alignItems={"center"}>
        <Button
          style={{ textTransform: "none", color: "#e01600", marginTop: "30px" }}
        >
          <FormattedMessage id="cancelSubscription" />
        </Button>
      </Box> */}
    </Box>
  );
}
